import React, { useState, useEffect } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Plus, Check } from "lucide-react";
import { workspacesApi } from "@/lib/apiClient";
import { WS_ICON_ORDER, WS_COLOR_ORDER, iconFor, accentFor } from "./workspaceMeta";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/context/LanguageContext";
import { toast } from "sonner";

export const CreateWorkspaceDialog = ({ onSaved, existing, trigger, open: controlledOpen, onOpenChange }) => {
    const { t } = useTranslation();
    const [uncontrolled, setUncontrolled] = useState(false);
    const open = controlledOpen !== undefined ? controlledOpen : uncontrolled;
    const setOpen = onOpenChange || setUncontrolled;
    const isEdit = !!existing;

    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [icon, setIcon] = useState("brain");
    const [color, setColor] = useState("violet");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open) {
            setName(existing?.name || "");
            setDescription(existing?.description || "");
            setIcon(existing?.icon || "brain");
            setColor(existing?.color || "violet");
        }
    }, [open, existing]);

    const save = async () => {
        if (!name.trim()) { toast.error(t("workspace.create.nameRequired")); return; }
        setBusy(true);
        try {
            const payload = { name: name.trim(), description: description.trim(), icon, color };
            const ws = isEdit ? await workspacesApi.update(existing.id, payload) : await workspacesApi.create(payload);
            toast.success(isEdit ? t("workspace.create.updated") : t("workspace.create.created"));
            onSaved?.(ws);
            setOpen(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("workspace.create.failed"));
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
            <DialogContent className="border-border bg-surface-1 sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-sans-display text-t1">{isEdit ? t("workspace.create.editTitle") : t("workspace.create.title")}</DialogTitle>
                    <DialogDescription className="sr-only">{t("workspace.hubSubtitle")}</DialogDescription>
                </DialogHeader>
                <div className="mt-2 space-y-4">
                    <div className="space-y-1.5">
                        <Label className="text-t2">{t("workspace.create.nameLabel")}</Label>
                        <Input data-testid="ws-create-name" value={name} onChange={(e) => setName(e.target.value)} placeholder={t("workspace.create.namePlaceholder")} className="bg-surface-2" />
                    </div>
                    <div className="space-y-1.5">
                        <Label className="text-t2">{t("workspace.create.descLabel")}</Label>
                        <Textarea data-testid="ws-create-desc" value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t("workspace.create.descPlaceholder")} className="min-h-[70px] bg-surface-2" />
                    </div>
                    <div className="space-y-2">
                        <Label className="text-t2">{t("workspace.create.iconLabel")}</Label>
                        <div className="flex flex-wrap gap-1.5">
                            {WS_ICON_ORDER.map((ic) => {
                                const Ic = iconFor(ic);
                                return (
                                    <button key={ic} data-testid={`ws-icon-${ic}`} onClick={() => setIcon(ic)}
                                        className={cn("flex h-9 w-9 items-center justify-center rounded-lg border transition-colors", icon === ic ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/10 text-t1" : "border-border bg-surface-2 text-t3 hover:text-t1")}>
                                        <Ic className="h-4 w-4" />
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div className="space-y-2">
                        <Label className="text-t2">{t("workspace.create.colorLabel")}</Label>
                        <div className="flex flex-wrap gap-2">
                            {WS_COLOR_ORDER.map((cl) => (
                                <button key={cl} data-testid={`ws-color-${cl}`} onClick={() => setColor(cl)}
                                    className={cn("flex h-8 w-8 items-center justify-center rounded-full transition-transform", color === cl && "ring-2 ring-offset-2 ring-offset-surface-1")}
                                    style={{ background: accentFor(cl), boxShadow: color === cl ? `0 0 0 2px ${accentFor(cl)}` : undefined }}>
                                    {color === cl && <Check className="h-4 w-4 text-white" />}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
                <DialogFooter className="mt-2">
                    <Button variant="secondary" className="bg-surface-3" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
                    <Button data-testid="ws-create-submit" className="gap-2" disabled={busy} onClick={save}>
                        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {isEdit ? t("workspace.create.saveBtn") : t("workspace.create.createBtn")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
